import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { adsUrlSchema } from '../../../types/ads';
import useGetAdsAnalytics from '../../../hooks/marketing/useGetAdsAnalytics';
import useUpdateAdsUrl from '../../../hooks/marketing/useUpdateAdsUrl';
import useGetMarketingData from '../../../hooks/marketing/useGetMarketingData';
import { MARKETING_EMBED_BORDER_COLOR } from '../../../constants/marketing';
import CodeBlock, { type Token } from '../../global/CodeBlock';

const INSTALL_INSTRUCTION = (
  <>
    Add it once to your site&rsquo;s global header — most platforms have a{' '}
    <strong>Code Injection</strong>, <strong>Custom Code</strong>, or{' '}
    <strong>Header Scripts</strong> section in their settings. Not sure? Send it
    to your web developer.
  </>
);

function AdsUrlInput({
  adsUrl,
  onSave,
  isSaving,
}: {
  adsUrl: string | null | undefined;
  onSave: (url: string | null) => void;
  isSaving: boolean;
}) {
  const [editing, setEditing] = useState(!adsUrl);
  const [value, setValue] = useState(adsUrl ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (adsUrl) {
      setValue(adsUrl);
      setEditing(false);
    }
  }, [adsUrl]);

  const handleSave = async () => {
    const trimmed = value.trim() || null;
    try {
      await adsUrlSchema.validate({ adsUrl: trimmed });
      setError(null);
      onSave(trimmed);
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message);
    }
  };

  const readOnly = !editing || isSaving;

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid',
            borderColor: error
              ? 'error.main'
              : editing
              ? 'primary.main'
              : 'divider',
            borderRadius: '6px',
            px: 1.25,
            height: 30,
            bgcolor: readOnly
              ? 'action.disabledBackground'
              : 'background.paper',
            transition: 'border-color 0.15s',
            '&:focus-within': {
              borderColor: error ? 'error.main' : 'primary.main',
            },
          }}>
          <InputBase
            placeholder="https://yourwebsite.com"
            value={value}
            onChange={e => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={e => e.key === 'Enter' && value.trim() && handleSave()}
            inputProps={{ readOnly }}
            autoFocus={editing && !adsUrl}
            sx={{
              fontSize: 'caption.fontSize',
              width: 260,
              cursor: readOnly ? 'default' : undefined,
              '& input': {
                p: 0,
                height: 'auto',
                cursor: readOnly ? 'default' : undefined,
              },
            }}
          />
        </Box>
        {editing ? (
          <>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={
                isSaving || !value.trim() || value.trim() === (adsUrl ?? '')
              }
              disableElevation
              sx={{
                height: 30,
                fontSize: 'caption.fontSize',
                textTransform: 'none',
                px: 1.5,
              }}>
              {isSaving ? <CircularProgress size={12} /> : 'Save'}
            </Button>
            {adsUrl && (
              <Button
                size="small"
                onClick={() => {
                  setValue(adsUrl);
                  setError(null);
                  setEditing(false);
                }}
                disabled={isSaving}
                sx={{
                  height: 30,
                  fontSize: 'caption.fontSize',
                  textTransform: 'none',
                  color: 'text.secondary',
                }}>
                Cancel
              </Button>
            )}
          </>
        ) : (
          <Button
            size="small"
            onClick={() => setEditing(true)}
            sx={{
              height: 30,
              fontSize: 'overline.fontSize',
              textTransform: 'none',
              color: 'text.secondary',
            }}>
            Change
          </Button>
        )}
      </Stack>
      {error && (
        <Typography variant="caption" color="error.dark">
          {error}
        </Typography>
      )}
    </Stack>
  );
}

const LABEL_SX = {
  typography: 'overline',
  color: 'text.disabled',
  whiteSpace: 'nowrap',
  width: 108,
  flexShrink: 0,
};

export default function AdsSetupAccordion() {
  const { data: marketingData } = useGetMarketingData();
  const { data: analyticsData, isLoading: scriptLoading } =
    useGetAdsAnalytics();
  const { mutate: saveAdsUrl, isPending: isSaving } = useUpdateAdsUrl();

  const adsUrl = marketingData?.adsUrl ?? null;
  const scriptUrl = analyticsData?.scriptUrl;
  const crossDomains = analyticsData?.crossDomains ?? '';
  const src = scriptUrl ?? 'YOUR_TRACKING_SCRIPT_URL';
  const scriptTokens: Token[] = [
    ['tag', '<script '],
    ['kw', 'async'],
    ['dim', ' src='],
    ['str', `"${src}"`],
    ['dim', ' data-cross-domains='],
    ['str', `"${crossDomains}"`],
    ['tag', '></script>'],
  ];

  const [expanded, setExpanded] = useState(!marketingData?.adsUrl);

  const handleChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${MARKETING_EMBED_BORDER_COLOR}`,
        '&:before': { display: 'none' },
      }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        sx={{
          px: 3,
          minHeight: 44,
          '& .MuiAccordionSummary-content': { my: 0.75 },
        }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="caption" fontWeight={600}>
            Ad Tracking Setup
          </Typography>
          {!expanded && (
            <Typography variant="caption" color="text.tertiary">
              Add tracking to your site so we can measure ad performance
            </Typography>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{ px: 3, pt: 0.5, pb: 2.5, bgcolor: 'background.paper' }}>
        <Stack spacing={1.75}>
          <Typography variant="caption" color="text.secondary">
            Only needed if your ads send visitors to{' '}
            <strong>your own website</strong> before Porter — if they link
            directly to your Porter booking page, skip this.
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={LABEL_SX}>Your ads URL</Box>
            <AdsUrlInput
              adsUrl={adsUrl}
              onSave={url => saveAdsUrl(url)}
              isSaving={isSaving}
            />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={LABEL_SX}>Tracking script</Box>
            {!adsUrl ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '8px',
                  bgcolor: 'action.hover',
                  border: '1px dashed',
                  borderColor: 'divider',
                  flex: 1,
                }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'warning.main',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5 }}>
                  Enter your website URL above to generate your tracking script.
                </Typography>
              </Box>
            ) : scriptLoading ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CircularProgress size={11} />
                <Typography variant="caption" color="text.tertiary">
                  Generating…
                </Typography>
              </Stack>
            ) : (
              <CodeBlock language="html" tokens={scriptTokens} />
            )}
          </Stack>

          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Box sx={{ ...LABEL_SX, pt: 0.25 }}>How to add it</Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}>
              {INSTALL_INSTRUCTION}
            </Typography>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
